import { useEffect, useState, useCallback } from "react";
import { ArrowRight, Building2, CheckCircle, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { Link } from "react-router-dom";


import Hero1 from "../assests/hero/hero1.jpg";
import Hero2 from "../assests/hero/hero2.jpg";
import Hero3 from "../assests/hero/hero3.jpg";
import CTASection from "./CTA";

const images = [Hero1, Hero2, Hero3];

const services = [
    {
        title: "Residential & Commercial Construction",
        description: "Complete building solutions from ground up",
        delay: "0ms"
    },
    {
        title: "Renovations & Refurbishments",
        description: "Transform existing spaces with expert care",
        delay: "100ms"
    },
    {
        title: "Driveways & External Works",
        description: "Professional outdoor construction services",
        delay: "200ms"
    },
    {
        title: "Conservatories & Garden Rooms",
        description: "Bespoke extensions to enhance your space",
        delay: "300ms"
    },
    {
        title: "Civil & Structural Works",
        description: "Robust infrastructure solutions",
        delay: "400ms"
    },
    {
        title: "Project Management & Maintenance",
        description: "End-to-end project oversight and care",
        delay: "500ms"
    },
];

const Home = () => {
    const [current, setCurrent] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isVisible, setIsVisible] = useState(false);

    // Entrance animation
    useEffect(() => {
        setIsVisible(true);
    }, []);

    // Auto slider with play/pause control
    useEffect(() => {
        if (!isPlaying) return;

        const interval = setInterval(() => {
            setCurrent((prev) => (prev + 1) % images.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [isPlaying]);

    const goToSlide = useCallback((index) => {
        setCurrent(index);
    }, []);

    const goToPrevious = useCallback(() => {
        setCurrent((prev) => (prev - 1 + images.length) % images.length);
    }, []);

    const goToNext = useCallback(() => {
        setCurrent((prev) => (prev + 1) % images.length);
    }, []);

    const togglePlayPause = useCallback(() => {
        setIsPlaying((prev) => !prev);
    }, []);

    return (
        <div className="pt-20">

            {/* ================= HERO SLIDER ================= */}
            <section className="relative min-h-[70vh] lg:min-h-screen flex items-center overflow-hidden">

                {/* Background Slider */}
                {images.map((img, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 bg-cover bg-center transition-all duration-1000 ${index === current ? "opacity-100 scale-100" : "opacity-0 scale-105"
                            }`}
                        style={{ backgroundImage: `url(${img})` }}
                        aria-hidden={index !== current}
                    />
                ))}

                {/* Animated Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70 lg:bg-gradient-to-r lg:from-black/80 lg:via-black/60 lg:to-transparent"></div>

                {/* Content */}
                <div className="relative z-10 max-w-7xl mx-auto px-4 w-full grid lg:grid-cols-2 gap-12 items-center">

                    {/* LEFT CONTENT */}
                    <div
                        className={`text-white text-center lg:text-left transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                            }`}
                    >
                        <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight animate-fade-in">
                            We United Constructions Ltd
                        </h1>

                        <p className="mt-4 text-lg sm:text-xl lg:text-2xl text-[#F57C00] font-semibold animate-fade-in-delay-1">
                            Building With Strength, Innovation & Integrity
                        </p>

                        <p className="mt-6 text-gray-200 text-base sm:text-lg max-w-xl mx-auto lg:mx-0 animate-fade-in-delay-2">
                            High-quality residential, commercial, and civil construction
                            services delivered across the United Kingdom.
                        </p>

                        <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-4 animate-fade-in-delay-3">
                            <Link
                                to="/contact"
                                className="group flex items-center gap-2 px-6 py-3 bg-[#F57C00] text-white font-semibold rounded-lg hover:bg-[#e36f00] transition-all hover:shadow-lg hover:scale-105"
                            >
                                Get a Quote
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </Link>

                            <Link
                                to="/services"
                                className="group flex items-center gap-2 px-6 py-3 border-2 border-white rounded-lg font-semibold hover:bg-white hover:text-[#0B3C8A] transition-all hover:shadow-lg"
                            >
                                Our Services
                                <Building2 size={18} className="group-hover:scale-110 transition-transform" />
                            </Link>
                        </div>
                    </div>

                    {/* RIGHT STACKED IMAGES (DESKTOP ONLY) */}
                    <div
                        className={`hidden lg:block relative h-[520px] transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
                            }`}
                    >
                        <div className="absolute top-0 left-0 w-72 h-44 rounded-xl shadow-2xl overflow-hidden transform hover:scale-105 transition-transform duration-300 hover:z-30">
                            <img
                                src={Hero1}
                                className="w-full h-full object-cover"
                                alt="Construction project showcase"
                            />
                        </div>

                        <div className="absolute top-24 left-28 w-80 h-52 rounded-xl shadow-2xl overflow-hidden transform hover:scale-105 transition-transform duration-300 hover:z-30">
                            <img
                                src={Hero2}
                                className="w-full h-full object-cover"
                                alt="Construction project showcase"
                            />
                        </div>

                        <div className="absolute top-52 left-12 w-72 h-44 rounded-xl shadow-2xl overflow-hidden transform hover:scale-105 transition-transform duration-300 hover:z-30">
                            <img
                                src={Hero3}
                                className="w-full h-full object-cover"
                                alt="Construction project showcase"
                            />
                        </div>
                    </div>
                </div>
            </section>


            {/* ================= INTRO ================= */}
            <section className="bg-white py-10">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">

                        {/* LEFT CONTENT */}
                        <div className="animate-fade-in-up">
                            <h2 className="text-3xl lg:text-4xl font-bold text-[#082C5A] text-center lg:text-left">
                                Trusted Construction Experts in the UK
                            </h2>


                            <div className="mt-4 w-20 h-1 bg-[#F57C00] mx-auto lg:mx-0"></div>

                            <p className="mt-8 text-gray-600 text-lg leading-relaxed text-center lg:text-left">
                                We United Constructions Ltd is a trusted UK-based construction company
                                specialising in building, renovation, external works, and bespoke
                                construction solutions. We work with homeowners, landlords,
                                developers, and commercial clients to deliver projects that are
                                practical, durable, and visually impressive.
                            </p>

                            <p className="mt-6 text-gray-600 text-lg leading-relaxed text-center lg:text-left">
                                From the initial consultation through to final completion, our
                                experienced team manages every stage of the project. We focus on
                                quality workmanship, safety, and clear communication, ensuring all
                                works are completed in line with UK Building Regulations and industry
                                best practices.
                            </p>

                        </div>

                        {/* RIGHT IMAGE */}
                        <div className="relative group">
                            <div className="absolute inset-0 bg-[#0B3C8A]/10 rounded-2xl transform translate-x-4 translate-y-4"></div>

                            <img
                                src={Hero2}
                                alt="Construction expertise"
                                className="relative rounded-2xl shadow-xl object-cover w-full h-[360px] group-hover:scale-105 transition-transform duration-500"
                            />
                        </div>

                    </div>
                </div>
            </section>

            {/* ================= SERVICES ================= */}
            <section className="bg-gray-50 py-10">
                <div className="max-w-7xl mx-auto px-4">
                    <h2 className="text-3xl lg:text-4xl font-bold text-center text-[#082C5A]">
                        Our Core Services
                    </h2>

                    <div className="mt-4 w-20 h-1 bg-[#F57C00] mx-auto"></div>

                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white p-8 rounded-xl shadow-md hover:shadow-2xl 
                 transition-all duration-300 border-l-4 border-[#F57C00] 
                 hover:border-l-8 hover:-translate-y-2 animate-fade-in-up
                 flex flex-col"
                                style={{ animationDelay: service.delay }}
                            >
                                {/* Content */}
                                <div className="flex gap-4 items-start">
                                    <CheckCircle
                                        className="text-[#F57C00] flex-shrink-0 mt-1 group-hover:scale-110 transition-transform"
                                        size={24}
                                    />
                                    <div>
                                        <h3 className="text-lg font-semibold text-[#082C5A] group-hover:text-[#F57C00] transition-colors">
                                            {service.title}
                                        </h3>
                                        <p className="mt-2 text-gray-600 text-sm">
                                            {service.description}
                                        </p>
                                    </div>
                                </div>

                                {/* Read More Button */}
                                <div className="mt-auto flex justify-end pt-4">
                                    <Link
                                        to="/services"
                                        className="text-sm font-semibold text-[#F57C00] hover:underline"
                                    >
                                        Read More →
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </section>


            {/* ================= WHY CHOOSE US ================= */}
            <section className="py-16 md:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <div className="text-center mb-14">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#082C5A]">
                            Why Clients Trust We United Constructions
                        </h2>
                        <div className="mt-4 w-20 h-1 bg-[#F57C00] mx-auto"></div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                        {[
                            {
                                title: "Quality First Approach",
                                desc: "We use premium materials and follow strict quality control at every stage of construction.",
                                icon: "✓"
                            },
                            {
                                title: "Fully Compliant & Safe",
                                desc: "All projects meet UK Building Regulations, health & safety standards and are fully insured.",
                                icon: "🛡️"
                            },
                            {
                                title: "Transparent Pricing",
                                desc: "Clear, detailed quotations with no hidden costs — what we quote is what you pay.",
                                icon: "£"
                            },
                            {
                                title: "On-time Delivery",
                                desc: "Realistic timelines and disciplined project management to complete work as promised.",
                                icon: "⏱️"
                            },
                            {
                                title: "Aftercare Support",
                                desc: "We provide support after handover — snagging list completion & maintenance advice.",
                                icon: "🤝"
                            },
                            {
                                title: "Local UK Expertise",
                                desc: "Deep understanding of UK weather conditions, planning rules and material performance.",
                                icon: "🇬🇧"
                            }
                        ].map((item, i) => (
                            <div
                                key={i}
                                className="group bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:border-[#F57C00]/40 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                            >
                                <div className="text-4xl mb-4 text-[#F57C00] font-black opacity-30 group-hover:opacity-100 transition-opacity">
                                    {item.icon}
                                </div>
                                <h3 className="text-xl font-bold text-[#082C5A] mb-3">
                                    {item.title}
                                </h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <CTASection />

            <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-fade-in-delay-1 {
          animation: fade-in 0.8s ease-out 0.2s forwards;
          opacity: 0;
        }

        .animate-fade-in-delay-2 {
          animation: fade-in 0.8s ease-out 0.4s forwards;
          opacity: 0;
        }

        .animate-fade-in-delay-3 {
          animation: fade-in 0.8s ease-out 0.6s forwards;
          opacity: 0;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
      `}</style>
        </div>
    );
};

export default Home;